/* eslint-disable react/no-multi-comp */
import { useState } from 'react';

import useTranslation from '@/utils/hooks/translation/useTranslation';

// Hooks
import { post } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import { PROCESS_DASHBOARD_INVITATION } from '@/lib/routes/apiEndpoints';

// Components
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';

const DashboardInvitationList = ({
    total,
    invitations,
    mutateInvitations,
    toggle,
    invitationOpen,
    pendingCount,
    mutateDashboards
}) => {
    const { t } = useTranslation('dashboard');
    const [processState, setProcessState] = useState({
        isLoading: false,
        error: null
    });

    const processDashboardInvitation = async (status, id) => {
        if (!invitationOpen && status === 'decide_later') {
            setProcessState((prevState) => ({
                ...prevState,
                isLoading: true
            }));
        }

        try {
            await post(
                injectEndpointUrlParams(PROCESS_DASHBOARD_INVITATION, {
                    dashboardId: id
                }),
                {
                    status
                }
            );

            if (status === 'accepted') {
                await mutateDashboards();
            }

            if (invitationOpen) {
                toggle();
            }

            await mutateInvitations();

            setProcessState((prevState) => ({
                ...prevState,
                isLoading: false
            }));
        } catch (e) {
            setProcessState((prevState) => ({
                ...prevState,
                isLoading: false,
                error: true
            }));
        }
    };

    const handleClickDecideLater = () => {
        invitations.slice(0, pendingCount).forEach((invit) => {
            return processDashboardInvitation('decide_later', invit.id);
        });
    };

    return (
        <>
            <Modal open={invitationOpen} onDismiss={toggle}>
                <Modal.Body>
                    <Modal.Title>
                        {t('dashboard_invitation_modal', {
                            count: total ?? 0
                        })}
                    </Modal.Title>
                    <Modal.Content>
                        <div className="scrollbar flex max-h-[80vh] flex-col overflow-auto rounded-md bg-icoGray-100 dark:bg-icoDarkMode-anthracite">
                            {invitations?.length > 0 ? (
                                invitations?.map((dashboard) => (
                                    <div
                                        className="flex w-full justify-between p-8"
                                        key={dashboard.id}>
                                        <div>
                                            <div className="text-4">{dashboard.name}</div>
                                            <div className="font-GMedium text-xs text-icoGray-500">
                                                {t('dashboard_created_by', {
                                                    firstName: dashboard?.user?.first_name,
                                                    lastName: dashboard?.user?.last_name
                                                })}
                                            </div>
                                        </div>
                                        <ProcessButton
                                            id={dashboard.id}
                                            onClick={processDashboardInvitation}
                                        />
                                    </div>
                                ))
                            ) : (
                                <p className="p-4 text-sm text-icoGray-500">
                                    {t('dashboard_no_invitations')}
                                </p>
                            )}
                        </div>
                    </Modal.Content>
                </Modal.Body>
            </Modal>
            <Modal open={pendingCount > 0} onDismiss={handleClickDecideLater}>
                <Modal.Body>
                    <Modal.Title>
                        {t('dashboard_invitation_modal_later', {
                            count: total ?? 0
                        })}
                    </Modal.Title>
                    <Modal.Content>
                        <div className="scrollbar flex max-h-[80vh] flex-col overflow-auto rounded-md bg-icoGray-100 dark:bg-icoDarkMode-anthracite">
                            {invitations?.map((dashboard) => (
                                <div className="flex w-full justify-between p-8" key={dashboard.id}>
                                    <div>
                                        <div className="text-4">{dashboard.name}</div>
                                        <div className="font-GMedium text-xs text-icoGray-500">
                                            {t('dashboard_created_by', {
                                                firstName: dashboard?.user?.first_name,
                                                lastName: dashboard?.user?.last_name
                                            })}
                                        </div>
                                    </div>
                                    <ProcessButton
                                        id={dashboard.id}
                                        onClick={processDashboardInvitation}
                                    />
                                </div>
                            ))}
                        </div>
                    </Modal.Content>
                    <Modal.Footer className="justify-center">
                        <Button
                            isLoading={processState.isLoading}
                            onKeyDown={handleClickDecideLater}
                            onClick={handleClickDecideLater}
                            variant="neutral"
                            size="xs"
                            className="font-GMedium text-xs text-icoGray-500 underline">
                            {t('dashboard_invitation_decide_later')}
                        </Button>
                    </Modal.Footer>
                </Modal.Body>
            </Modal>
        </>
    );
};

export const ProcessButton = ({ id, onClick }) => {
    const { t } = useTranslation('dashboard');
    const [stateInvitation, setStateInvitation] = useState({
        loadingState: {
            declined: false,
            accepted: false
        },
        error: null
    });

    const handleClick = (status) => {
        onClick(status, id);
    };

    return (
        <div className="flex flex-col lg:flex-row">
            <Button
                variant="secondary"
                size="sm"
                className="mb-3 lg:mb-auto lg:mr-3"
                isLoading={stateInvitation.loadingState.declined}
                onClick={() => {
                    setStateInvitation((prevState) => ({
                        ...prevState,
                        loadingState: {
                            ...prevState.loadingState,
                            declined: true
                        }
                    }));
                    handleClick('declined');
                }}>
                {t('dashboard_invitation_deny')}
            </Button>
            <Button
                size="sm"
                isLoading={stateInvitation.loadingState.accepted}
                onClick={() => {
                    setStateInvitation((prevState) => ({
                        ...prevState,
                        loadingState: {
                            ...prevState.loadingState,
                            accepted: true
                        }
                    }));
                    handleClick('accepted');
                }}>
                {t('dashboard_invitation_accept')}
            </Button>
        </div>
    );
};

export default DashboardInvitationList;
